/**
 * Column Mapping API Service
 * Authenticates with OAuth2 client_credentials and calls the predict_columns endpoint.
 *
 * Both URLs are intentionally relative so all requests go through the Vite dev-server
 * proxy (vite.config.ts) — this avoids CORS entirely.  In production, configure your
 * reverse-proxy / nginx to forward /auth and /api to gateway-stage.optum.com.
 */

import { fetchWithRetry } from '../utils/fetchWithRetry';

const TOKEN_URL   = '/auth/oauth2/token';
const MAPPING_URL = '/api/dev/pdr/demo/ci/column-mapping-agent/v1/mapping/predict_columns';
const CLIENT_ID    = '5npKC91Du12Gp7ShUHz5X8J5zAJLdyC6';
const CLIENT_SECRET = 'UHCYtkxK@MCzmiGUVAIGezin40zb8LFD3Kx&';
const CONFIG_ID    = 'FULL_EPDL';

export interface ApiColumnMapping {
  source_column:    string;
  predicted_target: string;
  confidence_score: number;
  requires_review:  boolean;
}

interface PredictColumnsResponse {
  status_code: number;
  message:     string;
  data: {
    mappings: ApiColumnMapping[];
  };
}

/** Fetch a short-lived OAuth2 bearer token using client_credentials. */
async function fetchAccessToken(): Promise<string> {
  const body = new URLSearchParams({
    grant_type:    'client_credentials',
    client_id:     CLIENT_ID,
    client_secret: CLIENT_SECRET,
  });

  const res = await fetchWithRetry(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const tokenText = await res.text().catch(() => '');
  if (!res.ok) {
    throw new Error(`OAuth2 token request failed (${res.status}): ${tokenText}`);
  }

  // Guard against HTML error pages (indicates a missing reverse-proxy for /auth)
  if (tokenText.trimStart().startsWith('<')) {
    throw new Error(
      `OAuth2 token endpoint returned HTML instead of JSON (status ${res.status}). ` +
      'Check that the /auth proxy is configured correctly on the server.'
    );
  }

  let data: any;
  try {
    data = JSON.parse(tokenText);
  } catch {
    throw new Error(`OAuth2 token endpoint returned invalid JSON: ${tokenText.slice(0, 120)}`);
  }

  if (!data.access_token) throw new Error('OAuth2 response did not contain access_token');
  return data.access_token as string;
}

/**
 * Call the column-mapping predict endpoint.
 * @param sourceColumns - header names from the uploaded Excel file (first row)
 * @returns Array of per-column mappings from the AI model
 */
export async function predictColumns(sourceColumns: string[]): Promise<ApiColumnMapping[]> {
  const token = await fetchAccessToken();

  const res = await fetchWithRetry(MAPPING_URL, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      config_id:      CONFIG_ID,
      source_columns: sourceColumns,
    }),
  });

  const mappingText = await res.text().catch(() => '');

  if (!res.ok) {
    throw new Error(`Column mapping API failed (${res.status}): ${mappingText}`);
  }

  // Guard against HTML error pages (indicates a missing reverse-proxy for /api)
  if (mappingText.trimStart().startsWith('<')) {
    throw new Error(
      `Column mapping endpoint returned HTML instead of JSON (status ${res.status}). ` +
      'Check that the /api proxy is configured correctly on the server.'
    );
  }

  let payload: PredictColumnsResponse;
  try {
    payload = JSON.parse(mappingText);
  } catch {
    throw new Error(`Column mapping endpoint returned invalid JSON: ${mappingText.slice(0, 120)}`);
  }

  if (!payload.data?.mappings) {
    throw new Error('Column mapping API returned an unexpected response shape');
  }

  return payload.data.mappings;
}
